import * as React from "react"
import { useDispatch } from "react-redux"

import createSoundFile from "@podcastsoundboard/ps-lib/api/soundFiles/create"
import importVoicySound from "@podcastsoundboard/ps-lib/api/voicySounds/import"
import UploadFileDropzoneChip from "@podcastsoundboard/ps-lib/components/UploadFileDropzoneChip"
import VoicyBrowser from "@podcastsoundboard/ps-lib/components/VoicyBrowser/VoicyBrowser"

import { Chip } from "@mui/material"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import CircularProgress from "@mui/material/CircularProgress"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"

import importFreesound from "../../../../api/freesounds/import"
import fetchSoundFilesAPI from "../../../../api/soundfiles/search"
import bitsToBytesString from "../../../../helpers/soundFile/bitsToBytesString"
import { useAppSelector } from "../../../../redux"
import { addSnackbar } from "../../../../redux/snackbars"
import SoundFile from "../../../../types/SoundFile"
import FreesoundBrowser from "./FreesoundBrowser"

export default function SoundFilePicker({
  soundFileUuid,
  setName,
  setSoundFileUuid,
  color,
}: {
  soundFileUuid: string
  setName: (name: string) => void
  setSoundFileUuid: (uuid: string) => void
  color?: string
}) {
  const dispatch = useDispatch()
  const darkModeEnabled = useAppSelector((state) => state.darkModeEnabled)

  const [uploadProgress, setUploadProgress] = React.useState(0)
  const [uploading, setUploading] = React.useState(false)
  const [freesoundBrowserOpen, setFreesoundBrowserOpen] = React.useState(false)
  const [voicyBrowserOpen, setVoicyBrowserOpen] = React.useState(false)
  const [sizeWarningShown, setSizeWarningShown] = React.useState(false)

  const [soundFiles, setSoundFiles] = React.useState<SoundFile[]>([])
  const [soundFilesLoading, setSoundFilesLoading] = React.useState(true)

  const setSoundFile = React.useCallback(
    (soundFile) => {
      if (!soundFile) return
      setSoundFileUuid(soundFile.uuid)
      setName && setName(soundFile.name)
    },
    [setName, setSoundFileUuid],
  )

  const fetchSoundFiles = React.useCallback(() => {
    setSoundFilesLoading(true)
    return fetchSoundFilesAPI()
      .then((soundFiles) => {
        setSoundFiles(soundFiles)
        setSoundFilesLoading(false)
      })
      .catch((err) => {
        setSoundFilesLoading(false)
        console.error("Error searching sound files", err)
        const text =
          (err.response && err.response.data && err.response.data.error) ||
          "Failed to search sound files."
        dispatch(addSnackbar({ text }))
      })
  }, [dispatch])

  React.useEffect(() => {
    fetchSoundFiles()
  }, [fetchSoundFiles])

  // show warning about soundfile size
  React.useEffect(() => {
    if (soundFiles && soundFiles.length > 0 && soundFileUuid) {
      const foundSoundFile = soundFiles.filter(
        (s) => s.uuid === soundFileUuid,
      )[0]
      if (foundSoundFile && foundSoundFile.fileSizeBytes > 1000 * 1000) {
        setSizeWarningShown(true)
        return
      }
    }
    setSizeWarningShown(false)
  }, [soundFileUuid, soundFiles])

  const uploadFile = (file) => {
    if (!file) return console.error("File missing.")
    const formData = new FormData()

    const fileSizeBytes = file.size

    formData.append("file", file, file.name)
    formData.append("fileSizeBytes", fileSizeBytes)

    const onUploadProgress = (progressEvent) => {
      if (!isNaN(progressEvent.loaded)) {
        const u = progressEvent.loaded / fileSizeBytes
        setUploadProgress(u * 100)
      }
    }

    setUploading(true)
    createSoundFile(formData, { onUploadProgress })
      .then((soundFile) => {
        if (!isNaN(fileSizeBytes))
          setSizeWarningShown(fileSizeBytes > 1000 * 1000)

        return fetchSoundFiles().then(() => {
          setSoundFile(soundFile)
          setUploading(false)
        })
      })
      .catch((err) => {
        setUploading(false)
        console.error("Error uploading sound files", err)
        const text =
          (err.response && err.response.data && err.response.data.error) ||
          "Failed to upload sound file."
        dispatch(addSnackbar({ text }))
      })
  }

  const handleFreesoundSelected = (freesoundId) => {
    setFreesoundBrowserOpen(false)

    setUploading(true)
    setUploadProgress(100)

    importFreesound(freesoundId)
      .then((soundFile) => {
        const { fileSizeBytes } = soundFile
        if (!isNaN(fileSizeBytes))
          setSizeWarningShown(fileSizeBytes > 1000 * 1000)

        return fetchSoundFiles().then(() => {
          setSoundFile(soundFile)
          setUploading(false)
        })
      })
      .catch((err) => {
        setUploading(false)
        console.error("Error importing freesound", err)
        const text =
          (err.response && err.response.data && err.response.data.error) ||
          "Failed to import freesound."
        dispatch(addSnackbar({ text }))
      })
  }

  const importSelectedVoicySound = (clipId: string) => {
    setUploading(true)
    setUploadProgress(100)

    importVoicySound(clipId)
      .then((soundFile) => {
        const { fileSizeBytes } = soundFile
        if (!isNaN(fileSizeBytes))
          setSizeWarningShown(fileSizeBytes > 1000 * 1000)

        return fetchSoundFiles().then(() => {
          setSoundFile(soundFile)
          setUploading(false)
        })
      })
      .catch((err) => {
        setUploading(false)
        console.error("Error importing sound", err)
        const text =
          (err.response && err.response.data && err.response.data.error) ||
          "Failed to import sound."
        dispatch(addSnackbar({ id: "soundImportFailed", text }))
      })
  }

  const onChange = React.useCallback(
    (e) => {
      const soundFileUuid = e.target.value
      setSoundFile(soundFiles.filter((s) => s.uuid === soundFileUuid)[0])
    },
    [soundFiles, setSoundFile],
  )

  const soundFilesAvailable = soundFiles && soundFiles.length > 0

  return (
    <Box width="100%">
      <Box display="flex" alignItems="center" width="100%">
        <FreesoundBrowser
          open={freesoundBrowserOpen}
          onClose={() => setFreesoundBrowserOpen(false)}
          handleFreesoundSelected={handleFreesoundSelected}
        />

        <VoicyBrowser
          open={voicyBrowserOpen}
          onClose={() => setVoicyBrowserOpen(false)}
          handleSoundSelected={(clipId) => {
            setVoicyBrowserOpen(false)
            importSelectedVoicySound(clipId)
          }}
          onAddSnackbar={(text) => dispatch(addSnackbar({ text }))}
          darkModeEnabled={darkModeEnabled}
        />

        {soundFilesLoading && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress style={{ color }} sx={{ mr: 2 }} size={25} />
          </Box>
        )}

        <Box style={{ display: "flex", flexGrow: 1 }} width="100%">
          <FormControl fullWidth disabled={!soundFilesAvailable}>
            <InputLabel id="audioFiles">
              {soundFilesLoading || soundFilesAvailable
                ? "Sound files"
                : "Please upload or search for sound file"}
            </InputLabel>
            <Select
              fullWidth
              labelId="audioFiles"
              value={soundFilesLoading ? "placeholder" : soundFileUuid}
              label="Sound files"
              onChange={onChange}
            >
              {soundFiles &&
                soundFiles.map((c) => (
                  <MenuItem key={c.uuid} value={c.uuid}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                    >
                      <Typography
                        style={{
                          overflow: "hidden",
                          display: "block",
                          textOverflow: "ellipsis",
                        }}
                        variant="body1"
                      >
                        {c.name || "No name"}
                      </Typography>
                      <Typography
                        style={{
                          overflow: "hidden",
                          display: "block",
                          textOverflow: "ellipsis",
                          fontSize: "0.75rem",
                        }}
                        variant="body1"
                        color="text.secondary"
                      >
                        {c.fileSizeBytes != null &&
                          `${bitsToBytesString(c.fileSizeBytes)} - `}
                        {c.filename || "No filename"}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              {soundFilesLoading && (
                <MenuItem value="placeholder">
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                  >
                    <Typography
                      style={{
                        overflow: "hidden",
                        display: "block",
                        textOverflow: "ellipsis",
                      }}
                      variant="body1"
                    >
                      Loading...
                    </Typography>
                    <Typography
                      style={{
                        overflow: "hidden",
                        display: "block",
                        textOverflow: "ellipsis",
                        fontSize: "0.75rem",
                      }}
                      variant="body1"
                      color="text.secondary"
                    >
                      Loading..
                    </Typography>
                  </Box>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>

        {uploading && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress
              variant={uploadProgress >= 100 ? "indeterminate" : "determinate"}
              value={uploadProgress}
              style={{ color }}
              sx={{ m: 2 }}
              size={30}
            />
          </Box>
        )}
      </Box>
      {!uploading && (
        <Stack
          direction="row"
          alignItems="flex-start"
          gap="0.2rem"
          mt={1}
          flexWrap="wrap"
        >
          <UploadFileDropzoneChip
            disabled={uploading}
            onFile={uploadFile}
            onErrorMessage={(errorMessage) =>
              dispatch(addSnackbar({ text: errorMessage }))
            }
          />
          <Chip
            label="Voicy"
            size="medium"
            disabled={uploading}
            clickable
            onClick={() => setVoicyBrowserOpen(true)}
            icon={
              <img
                alt=""
                role="presentation"
                src="/img/voicy-logo.svg"
                style={{ height: "80%" }}
              />
            }
          />

          <Chip
            label="Freesound"
            size="medium"
            clickable
            disabled={uploading}
            onClick={() => setFreesoundBrowserOpen(true)}
            icon={
              <img
                alt=""
                role="presentation"
                src="/img/freesound-logo.png"
                style={{ height: "80%", borderRadius: "50%" }}
              />
            }
          />
        </Stack>
      )}

      {sizeWarningShown && (
        <Stack sx={{ width: "100%", mt: 1 }} spacing={2}>
          <Alert severity="warning">
            This file is larger than 1MB, so it may take longer to load.
          </Alert>
        </Stack>
      )}
    </Box>
  )
}
